import type { BackendModule, ReadCallback, Services, InitOptions } from 'i18next';

/**
 * Onda 8 / Fatia 8.2 — backend customizado do i18next que carrega JSONs de
 * locale SOB DEMANDA via `import.meta.glob` (nativo do Vite — zero
 * dependência nova; equivalente ao padrão `resourcesToBackend`, mas sem
 * instalar `i18next-resources-to-backend`).
 *
 * `import.meta.glob('./{en-US,es-ES}/*.json')` mapeia os JSONs de en-US/es-ES
 * (`./en-US/crm.json`, `./es-ES/common.json`, …) para uma função de import
 * dinâmico cada — o Rollup gera um chunk assíncrono independente por
 * arquivo, buscado só quando `read()` é chamado para aquele (idioma,
 * namespace) específico.
 *
 * pt-BR fica EAGER em `index.ts` (`resources`, ver lá) e é DELIBERADAMENTE
 * fora do glob (revisão sênior BAIXO — um glob genérico `./*\/*.json` gerava
 * chunks assíncronos dos JSONs de pt-BR que nunca eram buscados, já que o
 * i18next resolve pt-BR pelo `resources` in-memory antes de tocar no backend;
 * eram build morto). `partialBundledLanguages: true` faz o i18next tratar
 * `resources` como cache PARCIAL e cair aqui só para en-US/es-ES. Uma
 * combinação (idioma, namespace) fora do glob resolve para `{}` em `read()`
 * (i18next então usa o fallbackLng pt-BR), nunca lança.
 */
const localeModules = import.meta.glob<{ default: Record<string, unknown> }>(
  './{en-US,es-ES}/*.json',
);

function moduleKey(language: string, namespace: string): string {
  return `./${language}/${namespace}.json`;
}

export const lazyLocaleBackend: BackendModule = {
  type: 'backend',

  init(_services: Services, _backendOptions: object, _i18nextOptions: InitOptions) {
    // Sem estado/configuração própria — os imports dinâmicos já sabem seu
    // próprio caminho via import.meta.glob.
  },

  read(language: string, namespace: string, callback: ReadCallback) {
    const key = moduleKey(language, namespace);
    const importer = localeModules[key];
    if (!importer) {
      // Combinação (idioma, namespace) inexistente: reporta um recurso
      // vazio (não um erro) — i18next cai no fallbackLng para as chaves,
      // mesmo comportamento de um JSON vazio. Nunca lança.
      callback(null, {});
      return;
    }
    importer()
      .then((mod) => callback(null, mod.default))
      .catch((err: Error) => callback(err, null));
  },
};

export default lazyLocaleBackend;
